<template>
  <footer class="footer">
    <div class="container">
      <div class="footer__wrapper">
        <div class="footer__left">
          <router-link class="footer__logo" :to="translations.i18nRoute({ name: 'home' })">
            <img src="@images/logo.png" alt="logo">
          </router-link>
          <div class="footer__info">
            <a class="footer__info-link" :href="'mailto:' + company?.emailFrom">
              <h6>@</h6>
              <p>{{ company?.emailFrom }}</p>
            </a>
            <p class="footer__info-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M11.5317 12.4724C15.5208 16.4604 16.4258 11.8467 18.9656 14.3848C21.4143 16.8328 22.8216 17.3232 19.7192 20.4247C19.3306 20.737 16.8616 24.4943 8.1846 15.8197C-0.493478 7.144 3.26158 4.67244 3.57397 4.28395C6.68387 1.17385 7.16586 2.58938 9.61449 5.03733C12.1544 7.5765 7.54266 8.48441 11.5317 12.4724Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M14.353 6.04297C16.124 6.38697 17.508 7.77197 17.853 9.54297" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <p>{{ t('company.phone') }}</p>
            </p>
          </div>
          <base-language />
        </div>
        <div class="footer__columns">
          <div class="footer__column">
            <h6>{{ t('footer.column1') }}</h6>
            <p
                v-for="category in categories"
                :key="category.node.uuid"
            >
              {{ category.node.name }}
            </p>
          </div>
          <div class="footer__column">
            <h6>{{ t('footer.column2') }}</h6>
            <router-link :to="translations.i18nRoute({ name: 'about' })">{{ t('docs.aboutUs.title') }}</router-link>
            <router-link :to="translations.i18nRoute({ name: 'policy' })">{{ t('docs.policy.title') }}</router-link>
            <router-link :to="translations.i18nRoute({ name: 'terms' })">{{ t('docs.terms.title') }}</router-link>
            <router-link :to="translations.i18nRoute({ name: 'faq' })">{{ t('docs.faq.title') }}</router-link>
            <router-link :to="translations.i18nRoute({ name: 'description' })">{{ t('docs.description.title') }}</router-link>
          </div>
        </div>
      </div>
    </div>
    <div class="footer__bottom">
      <div class="container">
        <div class="footer__bottom-wrapper">
          <p>{{ actualYear }} © COMPANYNAME. {{ t('footer.rights') }}.</p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import translations from "@/core/helpers/translations.js";
import {useCompanyStore} from "@/stores/company.js";
import {computed} from "vue";
import {useI18n} from "vue-i18n";
import {useCategoryStore} from "@/stores/category.js";
import BaseLanguage from "@/components/base/base-language.vue";

const companyStore = useCompanyStore()
const categoryStore = useCategoryStore()
const {t} = useI18n()

const company = computed(() => {
  return companyStore.companyInfo ? companyStore.companyInfo : null
})

const companyName = computed(() => {
  if (!company.value?.companyName) return '';
  const [text] = company.value.companyName.split(',').map((part) => part.trim());
  return text;
});

const categories = computed(() => categoryStore.categories)

const actualYear = computed(() => new Date().getFullYear());
</script>

<style lang="scss" scoped>
.footer {
  border-top: 1px solid #05ABF3;
  background-color: #005F88;
  
  &__wrapper {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding-block: 80px 40px;
  }

  &__left {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  &__logo {
    width: 225px;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &-link {
      display: flex;
      align-items: center;
      gap: 10px;

      @include hover {
        & p {
          color: #c5c5c5;
        }
      }

      & h6 {
        color: $white;
        font-size: 24px;
        font-weight: 500;
        line-height: 29.412px;
      }

      & p {
        transition: 0.2s;
        color: $white;
        font-weight: 600;
        line-height: 20px;
      }
    }
  }

  &__columns {
    display: flex;
    align-items: flex-start;
    gap: 75px
  }

  &__column {
    display: flex;
    flex-direction: column;
    gap: 10px;

    & h6 {
      padding-bottom: 10px;
      border-bottom: 3px solid $white;

      color: $white;
      font-size: 20px;
      font-weight: 600;
      line-height: 20px;
    }

    & p, a {
      cursor: pointer;
      transition: 0.2s;
      color: $white;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;

      @include hover {
        color: #b6b6b6;
      }
    }
  }

  &__bottom {
    &-wrapper {
      padding-block: 25px;
      border-top: 1px solid #05ABF3;

      & p {
        text-align: center;
        color: $white;
        font-size: 20px;
        font-weight: 400;
        line-height: 20px;
      }
    }
  }
}

@media (max-width: 900px) {
  .footer {
    &__wrapper {
      flex-direction: column;
      align-items: center;
      gap: 50px;
    }

    &__left {
      align-items: center;
    }
  }
}

@media (max-width: 600px) {
  .footer {
    &__columns {
      flex-direction: column;
      align-items: center;
      gap: 50px;
    }

    &__column {
      align-items: center;
    }

    &__bottom {
      &-wrapper {
        & p {
          font-size: 14px;
        }
      }
    }
  }
}
</style>